import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Voicemail,
  Play, Pause, Trash2, Check, Clock, User, RefreshCw, Volume2, Search,
  Briefcase, X, ChevronRight, ExternalLink, Download, Loader2, MessageSquare,
  Calendar as CalendarIcon,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { API } from "../../App";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import { useTimezone } from "../../contexts/TimezoneContext";
import { Link } from "react-router-dom";
import QuickSmsModal from "../../components/sms/QuickSmsModal";
import RecordingsTab from "../../components/calls/RecordingsTab";
import InlineRecordingPlayer from "../../components/calls/InlineRecordingPlayer";
import CallRecordingPlayer from "../../components/audio/CallRecordingPlayer";
import { Building2 } from "lucide-react";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const CallLogsPage = () => {
  const { formatDateTime } = useTimezone();
  const [calls, setCalls] = useState([]);
  const [voicemails, setVoicemails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get("tab") === "voicemails" ? "voicemails" : "calls");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVoicemail, setSelectedVoicemail] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  // Business/location filtering (multi-location accounts)
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [directionFilter, setDirectionFilter] = useState(() => new URLSearchParams(window.location.search).get("filter") === "missed" ? "missed" : "all");
  const [selectedCall, setSelectedCall] = useState(null);
  const [callDetail, setCallDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [usageLimits, setUsageLimits] = useState(null);
  // Date filter for the calls list
  const [dateFilter, setDateFilter] = useState("all"); // all | today | 7d | 30d | custom
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  // Quick-SMS composer state
  const [smsModal, setSmsModal] = useState({ open: false, toNumber: "", fromNumber: "", customerName: "" });

  // Inline-row recording playback — only one row can play at a time. Holds
  // the call_id of the active row, plus a ref to its <audio> so we can pause
  // it before another row takes over.
  const [inlinePlayingId, setInlinePlayingId] = useState(null);
  const inlineAudioRef = useRef(null);
  const setInlineAudioRef = (el) => { inlineAudioRef.current = el; };
  const handleInlineToggle = (call) => {
    const id = call.call_id || call.call_sid || call.twilio_sid;
    setInlinePlayingId((current) => {
      if (current === id) return null;     // toggle off the active row
      // Switching rows: pause the previous one before swapping.
      if (inlineAudioRef.current) {
        try { inlineAudioRef.current.pause(); } catch { /* element already gone */ }
      }
      return id;
    });
  };

  useEffect(() => {
    fetchData("");
    axios.get(`${API}/store/locations`, getAuthConfig())
      .then((r) => setLocations(r.data?.locations || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { fetchData(searchQuery); }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery, dateFilter, customStart, customEnd, locationFilter, directionFilter]);

  const getDateRangeParams = () => {
    const fmt = (d) => format(d, "yyyy-MM-dd");
    const today = new Date();
    if (dateFilter === "today") return { start: fmt(today), end: fmt(today) };
    if (dateFilter === "7d") return { start: fmt(new Date(Date.now() - 6 * 86400000)), end: fmt(today) };
    if (dateFilter === "30d") return { start: fmt(new Date(Date.now() - 29 * 86400000)), end: fmt(today) };
    if (dateFilter === "custom") return { start: customStart || null, end: customEnd || null };
    return { start: null, end: null };
  };

  const fetchData = async (searchTerm) => {
    setLoading(true);
    try {
      const config = getAuthConfig();
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : "";
      const { start, end } = getDateRangeParams();
      const dateParams = `${start ? `&start_date=${start}` : ""}${end ? `&end_date=${end}` : ""}`;
      const locParam = locationFilter !== "all" ? `&location_id=${encodeURIComponent(locationFilter)}` : "";
      const dirParam = directionFilter === "missed" ? "&status=missed" : directionFilter !== "all" ? `&direction=${directionFilter}` : "";
      const [callsRes, voicemailsRes, statsRes, limitsRes] = await Promise.all([
        axios.get(`${API}/calls?limit=500${searchParam}${dateParams}${locParam}${dirParam}`, config),
        axios.get(`${API}/voicemails`, config),
        axios.get(`${API}/calls/stats`, config),
        axios.get(`${API}/usage/current`, config).then(() => axios.get(`${API}/usage/limits`, config)),
      ]);
      setCalls(callsRes.data || []);
      setVoicemails(voicemailsRes.data || []);
      setStats(statsRes.data);
      setUsageLimits(limitsRes.data);
    } catch {
      toast.error("Failed to load call history");
    } finally { setLoading(false); }
  };

  const openCallDetail = async (call) => {
    setSelectedCall(call);
    setCallDetail(null);
    setDetailLoading(true);
    // Old rows might not have call_id — fall back to call_sid / twilio_sid
    // so the modal still opens for legacy data.
    const lookupId = call.call_id || call.call_sid || call.twilio_sid;
    if (!lookupId) {
      setCallDetail(call);
      setDetailLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/voice/calls/${lookupId}`, getAuthConfig());
      setCallDetail(res.data);
    } catch {
      setCallDetail(call);
    } finally { setDetailLoading(false); }
  };

  // Build a proxied audio URL for the recording so Twilio creds aren't prompted
  const getRecordingProxyUrl = (call) => {
    const token = localStorage.getItem("token");
    const lookupId = call?.call_id || call?.call_sid || call?.twilio_sid;
    if (!lookupId || !token) return null;
    return `${API}/voice/calls/${lookupId}/recording?token=${encodeURIComponent(token)}`;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 11 && cleaned.startsWith("1")) return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    if (cleaned.length === 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    return phone;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getCallIcon = (call) => {
    if (call.direction === "outbound") return <PhoneOutgoing className="w-4 h-4 text-blue-400" />;
    if (call.status === "no-answer" || call.status === "busy" || call.status === "failed") return <PhoneMissed className="w-4 h-4 text-red-400" />;
    return <PhoneIncoming className="w-4 h-4 text-green-400" />;
  };

  // Open the quick-SMS composer from a call row.
  // For inbound calls: business number = call.to_number, recipient = call.from_number
  // For outbound calls: business number = call.from_number, recipient = call.to_number
  const openSmsModalFromCall = (e, call) => {
    e.stopPropagation();
    const isInbound = call.direction === "inbound";
    const toNumber = isInbound ? call.from_number : call.to_number;
    const fromNumber = isInbound ? call.to_number : call.from_number;
    if (!toNumber || !fromNumber) {
      toast.error("Missing phone number on this call");
      return;
    }
    setSmsModal({
      open: true,
      toNumber,
      fromNumber,
      customerName: call.customer_name || "",
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: "bg-green-500/20 text-green-400",
      "in-progress": "bg-blue-500/20 text-blue-400",
      ringing: "bg-yellow-500/20 text-yellow-400",
      queued: "bg-gray-500/20 text-gray-400",
      busy: "bg-orange-500/20 text-orange-400",
      "no-answer": "bg-red-500/20 text-red-400",
      failed: "bg-red-500/20 text-red-400",
    };
    return styles[status] || "bg-gray-500/20 text-gray-400";
  };

  const playVoicemail = async (voicemail) => {
    setSelectedVoicemail(voicemail);
    if (!voicemail.is_listened) {
      try {
        await axios.post(`${API}/voicemails/${voicemail.voicemail_id}/listened`, {}, getAuthConfig());
        setVoicemails(prev => prev.map(vm => vm.voicemail_id === voicemail.voicemail_id ? { ...vm, is_listened: true } : vm));
        setStats(prev => prev ? { ...prev, unlistened_voicemails: Math.max(0, prev.unlistened_voicemails - 1) } : prev);
      } catch {}
    }
  };

  const deleteVoicemail = async () => {
    if (!deleteDialog.id) return;
    try {
      await axios.delete(`${API}/voicemails/${deleteDialog.id}`, getAuthConfig());
      setVoicemails(prev => prev.filter(vm => vm.voicemail_id !== deleteDialog.id));
      setDeleteDialog({ open: false, id: null });
      if (selectedVoicemail?.voicemail_id === deleteDialog.id) setSelectedVoicemail(null);
      toast.success("Voicemail deleted");
    } catch { toast.error("Failed to delete voicemail"); }
  };

  const unlistenedCount = voicemails.filter(vm => !vm.is_listened).length;
  const detail = callDetail || selectedCall;

  return (
    <div className="space-y-6" data-testid="call-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground font-['Manrope']">Call History</h2>
          <p className="text-muted-foreground">View calls, voicemails, and call statistics</p>
        </div>
        <Button variant="outline" onClick={() => fetchData(searchQuery)} className="border-border text-foreground">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Minutes Used This Month - Progress Bar */}
      {usageLimits?.limits?.minutes && (() => {
        const m = usageLimits.limits.minutes;
        const bp = usageLimits.billing_period;
        const periodText = bp?.start && bp?.end
          ? `${format(new Date(bp.start), "MMM d, yyyy")} – ${format(new Date(bp.end), "MMM d, yyyy")}`
          : null;
        if (m.unlimited) {
          return (
            <Card className="border-border bg-card" data-testid="minutes-usage-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Minutes Used This Month</p>
                    {periodText && <p className="text-xs text-muted-foreground/70" data-testid="minutes-period-dates">{periodText}</p>}
                    <p className="text-2xl font-bold text-foreground mt-0.5">{m.used} min</p>
                  </div>
                  <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">Unlimited Plan</Badge>
                </div>
              </CardContent>
            </Card>
          );
        }
        const pct = Math.min(100, Math.round((m.used / Math.max(m.limit, 1)) * 100));
        const barColor = pct >= 90 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-emerald-500";
        const textColor = pct >= 90 ? "text-red-600" : pct >= 75 ? "text-amber-600" : "text-emerald-600";
        const remaining = Math.max(0, m.limit - m.used);
        return (
          <Card className="border-border bg-card" data-testid="minutes-usage-card">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-end justify-between gap-2 mb-2">
                <div>
                  <p className="text-sm text-muted-foreground">Minutes Used This Month</p>
                  {periodText && (
                    <p className="text-xs text-muted-foreground/70" data-testid="minutes-period-dates">
                      Billing period: {periodText}
                    </p>
                  )}
                  <p className="text-2xl font-bold text-foreground mt-0.5">
                    <span data-testid="minutes-used-value">{m.used}</span>
                    <span className="text-muted-foreground/70 text-base font-medium"> / {m.limit} min</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-semibold ${textColor}`} data-testid="minutes-percent">{pct}% used</p>
                  <p className="text-xs text-muted-foreground">{remaining} min remaining</p>
                </div>
              </div>
              <div className="w-full h-2 rounded-full bg-muted overflow-hidden" data-testid="minutes-progress-bar">
                <div className={`h-full ${barColor} transition-all duration-500 ease-out`} style={{ width: `${pct}%` }} />
              </div>
              {pct >= 90 && (
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-red-600">You're almost out of minutes. Upgrade to avoid overages.</p>
                  <Link to="/dashboard/settings/billing" data-testid="upgrade-plan-link">
                    <Button size="sm" className="h-7 text-xs">Upgrade Plan</Button>
                  </Link>
                </div>
              )}
              {pct >= 75 && pct < 90 && (
                <p className="mt-2 text-xs text-amber-600">Heads up — you're at {pct}% of your monthly minutes.</p>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-border bg-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center"><Phone className="w-5 h-5 text-blue-500" /></div>
              <div><p className="text-muted-foreground text-sm">Total Calls</p><p className="text-xl font-bold text-foreground">{stats.total_calls}</p></div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center"><Clock className="w-5 h-5 text-green-500" /></div>
              <div>
                <p className="text-muted-foreground text-sm">Talk Time <span className="text-xs opacity-60">(this month)</span></p>
                <p className="text-xl font-bold text-foreground">{stats.total_duration_formatted}</p>
                {stats.lifetime_duration_formatted && stats.lifetime_duration_seconds !== stats.total_duration_seconds && (
                  <p className="text-[11px] text-muted-foreground/70 mt-0.5">Lifetime: {stats.lifetime_duration_formatted}</p>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center"><Voicemail className="w-5 h-5 text-purple-500" /></div>
              <div><p className="text-muted-foreground text-sm">Voicemails</p><p className="text-xl font-bold text-foreground">{stats.total_voicemails}</p></div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center"><PhoneMissed className="w-5 h-5 text-red-500" /></div>
              <div><p className="text-muted-foreground text-sm">Missed</p><p className="text-xl font-bold text-foreground">{stats.by_status?.["no-answer"] || 0}</p></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calls per Job Source — numbers assigned to a source in Phone settings */}
      {stats?.by_source?.length > 0 && (
        <Card className="border-border bg-card" data-testid="calls-by-source-card">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center"><Briefcase className="w-4 h-4 text-emerald-500" /></div>
              <p className="text-muted-foreground text-sm font-medium">Calls by Source</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {stats.by_source.map((s) => (
                <span key={s.source_id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-xs" data-testid={`source-call-stat-${s.source_id}`}>
                  <span className="text-foreground font-medium">{s.source_name || "Unknown source"}</span>
                  <span className="font-bold text-emerald-500">{s.calls}</span>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted border border-border p-1">
          <TabsTrigger value="calls" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-muted-foreground">
            <Phone className="w-4 h-4 mr-2" /> All Calls
          </TabsTrigger>
          <TabsTrigger value="voicemails" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-muted-foreground relative">
            <Voicemail className="w-4 h-4 mr-2" /> Voicemails
            {unlistenedCount > 0 && <Badge className="ml-2 bg-red-500 text-white text-xs h-5 min-w-5">{unlistenedCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="recordings" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-muted-foreground" data-testid="recordings-tab-trigger">
            <Volume2 className="w-4 h-4 mr-2" /> Recordings
          </TabsTrigger>
        </TabsList>

        {/* All Calls Tab */}
        <TabsContent value="calls" className="mt-4">
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, phone, business number, or location..." className="pl-9 h-10" data-testid="call-search-input" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {locations.length > 0 && (
                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger className="w-44 h-10" data-testid="call-location-filter">
                    <Building2 className="w-4 h-4 mr-1.5 text-muted-foreground" />
                    <SelectValue placeholder="All locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All locations</SelectItem>
                    {locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Select value={directionFilter} onValueChange={setDirectionFilter}>
                <SelectTrigger className="w-32 h-10" data-testid="call-direction-filter">
                  <SelectValue placeholder="All calls" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All calls</SelectItem>
                  <SelectItem value="inbound">Inbound</SelectItem>
                  <SelectItem value="outbound">Outbound</SelectItem>
                  <SelectItem value="missed">Missed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-40 h-10" data-testid="call-date-filter">
                  <CalendarIcon className="w-4 h-4 mr-1.5 text-muted-foreground" />
                  <SelectValue placeholder="All dates" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All dates</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
              {dateFilter === "custom" && (
                <>
                  <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                    className="h-10 w-36" data-testid="call-date-start" />
                  <span className="text-muted-foreground text-sm">–</span>
                  <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                    className="h-10 w-36" data-testid="call-date-end" />
                </>
              )}
            </div>
          </div>
          <Card className="border-border bg-card">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-muted-foreground">Loading calls...</div>
              ) : calls.length === 0 ? (
                <div className="p-8 text-center">
                  <Phone className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">{searchQuery ? "No calls match your search" : "No call history yet"}</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="divide-y divide-border">
                    {calls.map((call, idx) => {
                      const mainPhone = call.direction === "inbound" ? call.from_number : call.to_number;
                      const customerName = call.customer_name;
                      const hasRecording = !!(call.recording_url);
                      const inlineKey = call.call_id || call.call_sid || call.twilio_sid || idx;
                      return (
                        <div key={inlineKey} data-testid={`call-row-wrapper-${idx}`}>
                        <button
                          onClick={() => openCallDetail(call)}
                          className="w-full text-left p-4 hover:bg-muted/50 transition-colors group/call"
                          data-testid={`call-row-${idx}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                                {getCallIcon(call)}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-foreground" data-testid={`call-display-${idx}`}>
                                    {customerName ? (<>{customerName}<span className="text-muted-foreground font-normal ml-1.5 text-sm">{formatPhoneNumber(mainPhone)}</span></>) : formatPhoneNumber(mainPhone)}
                                  </span>
                                  {call.has_voicemail && <Badge className="bg-purple-500/15 text-purple-600 dark:text-purple-400 text-xs"><Voicemail className="w-3 h-3 mr-1" /> VM</Badge>}
                                  {call.job_source_name && <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-xs" data-testid={`call-source-${idx}`}><Briefcase className="w-3 h-3 mr-1" /> {call.job_source_name}</Badge>}
                                </div>
                                <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                                  <span>{call.direction === "inbound" ? "Incoming" : "Outgoing"}</span>
                                  {/* Which business/location handled this call + the business number */}
                                  {(call.location_name || call.business_number || (call.direction === "inbound" ? call.to_number : call.from_number)) && (
                                    <>
                                      <span className="text-muted-foreground/40">&#8226;</span>
                                      <span className="flex items-center gap-1" data-testid={`call-business-number-${idx}`}>
                                        <Building2 className="w-3 h-3 text-muted-foreground/60" />
                                        <span className="text-xs">
                                          {call.direction === "inbound" ? "Called: " : "From: "}
                                          {call.location_name && (
                                            <span className="font-medium text-foreground/80" data-testid={`call-location-${idx}`}>{call.location_name}</span>
                                          )}
                                          {call.location_name ? <span className="text-muted-foreground/40"> · </span> : null}
                                          <span className="font-mono">
                                            {formatPhoneNumber(call.business_number || (call.direction === "inbound" ? call.to_number : call.from_number))}
                                          </span>
                                        </span>
                                      </span>
                                    </>
                                  )}
                                  {call.duration > 0 && (<><span className="text-muted-foreground/40">&#8226;</span><span>{formatDuration(call.duration)}</span></>)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => openSmsModalFromCall(e, call)}
                                onKeyDown={(e) => { if (e.key === "Enter") openSmsModalFromCall(e, call); }}
                                className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-blue-500/10 hover:text-blue-500 transition-colors cursor-pointer"
                                title="Send SMS"
                                data-testid={`call-row-sms-btn-${idx}`}
                              >
                                <MessageSquare className="w-4 h-4" />
                              </span>
                              <div className="text-right">
                                <Badge className={getStatusBadge(call.status)}>{call.status || "unknown"}</Badge>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {call.created_at && formatDistanceToNow(new Date(call.created_at), { addSuffix: true })}
                                </p>
                                {call.created_at && (
                                  <p className="text-[11px] text-muted-foreground/70" data-testid={`call-datetime-${idx}`}>
                                    {formatDateTime(call.created_at)}
                                  </p>
                                )}
                              </div>
                              <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover/call:text-foreground transition-colors" />
                            </div>
                          </div>
                        </button>
                        {hasRecording && (
                          <InlineRecordingPlayer
                            call={call}
                            getRecordingProxyUrl={getRecordingProxyUrl}
                            formatDuration={formatDuration}
                            isPlaying={inlinePlayingId === inlineKey}
                            onTogglePlay={() => handleInlineToggle(call)}
                            audioRefSetter={inlinePlayingId === inlineKey ? setInlineAudioRef : undefined}
                            onEnded={() => setInlinePlayingId(null)}
                          />
                        )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voicemails Tab */}
        <TabsContent value="voicemails" className="mt-4">
          <div className="grid gap-4" style={{ gridTemplateColumns: selectedVoicemail ? "1fr 1fr" : "1fr" }}>
            <Card className="border-border bg-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-foreground text-lg">Voicemails</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {voicemails.length === 0 ? (
                  <div className="p-8 text-center"><Voicemail className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" /><p className="text-muted-foreground">No voicemails</p></div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <div className="divide-y divide-border">
                      {voicemails.map((vm) => (
                        <div key={vm.voicemail_id} data-testid={`voicemail-row-${vm.voicemail_id}`} onClick={() => playVoicemail(vm)}
                          className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${selectedVoicemail?.voicemail_id === vm.voicemail_id ? "bg-muted/50" : ""} ${!vm.is_listened ? "border-l-2 border-l-blue-500" : ""}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center"><Voicemail className="w-4 h-4 text-purple-500" /></div>
                              <div>
                                <p className="font-medium text-foreground">{vm.customer_name || formatPhoneNumber(vm.from_number)}</p>
                                <p className="text-xs text-muted-foreground">{formatDuration(vm.duration)} &#8226; {vm.created_at && formatDistanceToNow(new Date(vm.created_at), { addSuffix: true })}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {!vm.is_listened && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, id: vm.voicemail_id }); }}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          {vm.recording_expired && (
                            <p className="mt-1 text-xs text-amber-500" data-testid={`voicemail-expired-${vm.voicemail_id}`}>Recording expired at provider</p>
                          )}
                          {vm.transcription && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{vm.transcription}</p>}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {selectedVoicemail && (
              <Card className="border-border bg-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-foreground text-lg">Voicemail Player</CardTitle>
                    <Button variant="ghost" size="icon" onClick={() => setSelectedVoicemail(null)}><X className="w-4 h-4" /></Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center"><User className="w-6 h-6 text-purple-500" /></div>
                    <div>
                      <p className="font-medium text-foreground">{selectedVoicemail.customer_name || formatPhoneNumber(selectedVoicemail.from_number)}</p>
                      <p className="text-sm text-muted-foreground">{formatDuration(selectedVoicemail.duration)} &#8226; {selectedVoicemail.created_at && formatDistanceToNow(new Date(selectedVoicemail.created_at), { addSuffix: true })}</p>
                    </div>
                  </div>
                  {selectedVoicemail.recording_expired ? (
                    <div className="p-3 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground" data-testid="voicemail-expired-note">
                      This recording is no longer available — it was deleted or expired at the phone provider.
                    </div>
                  ) : selectedVoicemail.recording_url && (
                    <div className="p-3 rounded-lg bg-muted/50 border border-border" data-testid="voicemail-recording-section">
                      <CallRecordingPlayer
                        src={`${API}/voice/calls/${selectedVoicemail.voicemail_id}/recording?token=${encodeURIComponent(localStorage.getItem("token") || "")}`}
                        knownDuration={selectedVoicemail.duration || 0}
                        fileName={`Voicemail_${(selectedVoicemail.customer_name || selectedVoicemail.from_number || "call").replace(/[^a-z0-9]+/gi, "_")}_${selectedVoicemail.created_at ? format(new Date(selectedVoicemail.created_at), "yyyy-MM-dd") : "recording"}.mp3`}
                        testId="voicemail-player"
                      />
                    </div>
                  )}
                  {selectedVoicemail.transcription && (
                    <div className="mt-4 p-3 rounded-lg bg-muted/30 border border-border">
                      <p className="text-xs text-muted-foreground font-medium mb-1">Transcription</p>
                      <p className="text-sm text-foreground">{selectedVoicemail.transcription}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Recordings Tab */}
        <TabsContent value="recordings" className="mt-4">
          <RecordingsTab
            calls={calls}
            loading={loading}
            getRecordingProxyUrl={getRecordingProxyUrl}
            formatPhoneNumber={formatPhoneNumber}
            formatDuration={formatDuration}
          />
        </TabsContent>
      </Tabs>

      {/* Call Detail Dialog */}
      <Dialog open={!!selectedCall} onOpenChange={(open) => { if (!open) { setSelectedCall(null); setCallDetail(null); } }}>
        <DialogContent className="bg-background border-border max-w-lg p-0 gap-0" data-testid="call-detail-dialog">
          <DialogHeader className="p-5 pb-3 border-b border-border">
            <DialogTitle className="text-foreground flex items-center gap-2">
              {selectedCall && getCallIcon(selectedCall)}
              Call Details
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh]">
            <div className="p-5 space-y-4">
              {detailLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
              ) : detail ? (
                <>
                  {/* Call Info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                        <User className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground font-semibold text-lg">{detail.customer_name || formatPhoneNumber(detail.direction === "inbound" ? detail.from_number : detail.to_number)}</p>
                        <p className="text-sm text-muted-foreground">{formatPhoneNumber(detail.direction === "inbound" ? detail.from_number : detail.to_number)}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={(e) => openSmsModalFromCall(e, detail)}
                        data-testid="call-detail-sms-btn"
                      >
                        <MessageSquare className="w-4 h-4 mr-1.5 text-blue-500" /> SMS
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <InfoPill icon={<Phone className="w-3 h-3" />} label="Direction" value={detail.direction === "inbound" ? "Incoming" : "Outgoing"} />
                      <InfoPill
                        icon={<Phone className="w-3 h-3" />}
                        label={detail.direction === "inbound" ? "Called Number" : "Your Number"}
                        value={formatPhoneNumber(detail.business_number || (detail.direction === "inbound" ? detail.to_number : detail.from_number))}
                      />
                      {(detail.location_name || detail.phone_label) && (
                        <InfoPill
                          icon={<Building2 className="w-3 h-3" />}
                          label="Business / Location"
                          value={<span data-testid="call-detail-location">{[detail.location_name, detail.phone_label].filter(Boolean).join(" · ")}</span>}
                        />
                      )}
                      <InfoPill icon={<Clock className="w-3 h-3" />} label="Duration" value={formatDuration(detail.duration)} />
                      <InfoPill label="Status" value={<Badge className={`${getStatusBadge(detail.status)} text-xs`}>{detail.status}</Badge>} />
                      <InfoPill label="Date" value={detail.created_at ? format(new Date(detail.created_at), "MMM d, h:mm a") : "—"} />
                    </div>
                  </div>

                  {/* Recording Player */}
                  {detail.recording_url && (() => {
                    const proxyUrl = getRecordingProxyUrl(detail);
                    const dateStr = detail.created_at ? format(new Date(detail.created_at), "yyyy-MM-dd") : "recording";
                    const who = (detail.customer_name || (detail.direction === "inbound" ? detail.from_number : detail.to_number) || "call").replace(/[^a-z0-9]+/gi, "_");
                    return (
                    <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2" data-testid="call-recording-section">
                      <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                        <Volume2 className="w-4 h-4 text-blue-500" /> Call Recording
                        {(detail.recording_duration || 0) > 0 && (
                          <span className="text-xs text-muted-foreground font-normal">· Length {formatDuration(detail.recording_duration)}</span>
                        )}
                      </p>
                      <CallRecordingPlayer
                        src={proxyUrl}
                        knownDuration={detail.recording_duration || detail.duration || 0}
                        fileName={`Call_Recording_${who}_${dateStr}.mp3`}
                        testId="call-recording-player"
                      />
                    </div>
                    );
                  })()}

                  {/* No Recording */}
                  {!detail.recording_url && detail.status === "completed" && (
                    <div className="rounded-xl border border-dashed border-border p-4 text-center">
                      <Volume2 className="w-6 h-6 mx-auto text-muted-foreground/30 mb-1" />
                      <p className="text-xs text-muted-foreground">No recording available for this call</p>
                    </div>
                  )}

                  {/* Customer Jobs */}
                  {detail.customer_jobs && detail.customer_jobs.length > 0 && (
                    <div className="space-y-2" data-testid="call-customer-jobs">
                      <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                        <Briefcase className="w-4 h-4 text-amber-500" />
                        Jobs from this phone number ({detail.customer_jobs.length})
                      </p>
                      <div className="space-y-1.5">
                        {detail.customer_jobs.map((job) => (
                          <Link
                            key={job.job_id}
                            to={`/dashboard/jobs?job=${job.job_id}`}
                            onClick={() => { setSelectedCall(null); setCallDetail(null); }}
                            className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/20 hover:bg-muted/50 hover:border-amber-500/40 transition-colors group/job"
                            data-testid={`job-link-${job.job_id}`}
                          >
                            <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                              <Briefcase className="w-4 h-4 text-amber-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {(job.job_display_id || job.ticket_number) && (
                                  <span className="font-mono text-xs font-semibold text-blue-600 dark:text-blue-400 group-hover/job:underline">
                                    #{job.job_display_id || job.ticket_number}
                                  </span>
                                )}
                                <p className="text-sm font-medium text-foreground truncate">{job.title}</p>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                <Badge className={`${getStatusBadge(job.status)} text-[10px] h-4`}>{job.status}</Badge>
                                {job.scheduled_date && <span>{format(new Date(job.scheduled_date), "MMM d, yyyy")}</span>}
                                {job.assigned_to_name && <span>&#8226; {job.assigned_to_name}</span>}
                              </div>
                            </div>
                            {job.total_charged > 0 && <span className="text-sm font-semibold text-green-500 shrink-0">${job.total_charged}</span>}
                            <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover/job:text-foreground shrink-0" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Delete Voicemail Confirm */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, id: open ? deleteDialog.id : null })}>
        <AlertDialogContent className="bg-background border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Delete Voicemail</AlertDialogTitle>
            <AlertDialogDescription>This voicemail will be permanently deleted. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-foreground">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteVoicemail} className="bg-red-500 text-white hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick SMS Composer */}
      <QuickSmsModal
        open={smsModal.open}
        onClose={() => setSmsModal((s) => ({ ...s, open: false }))}
        toNumber={smsModal.toNumber}
        fromNumber={smsModal.fromNumber}
        customerName={smsModal.customerName}
      />
    </div>
  );
};

/* Small helper component */
const InfoPill = ({ icon, label, value }) => (
  <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border border-border">
    {icon}
    <div>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <div className="text-sm text-foreground font-medium">{value}</div>
    </div>
  </div>
);

export default CallLogsPage;
